import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, Input, Radio, Switch, Tag, Tooltip } from 'antd';
import { IconMapPins } from '@tabler/icons-react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { CrudMode } from '@ohpen/enso-ui';

import { TradingAddress } from '@pmp/api';
import { useRegionsAndBdms } from '@pmp/hooks';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField } from './AddressField';
import { BDMField } from './BDMField';
import styles from '../styles.module.css';

export const TradingNames: React.FC<TabProps> = ({ form, mode, selectsOptions, fcaModifiedFields, removeFcaModifiedField, errorRows, setDisableSave }) => {
  const { t } = useTranslation();
  const { regions, bdms, loading: regionsAndBdmsLoading } = useRegionsAndBdms();
  const [filter, setFilter] = useState<string>('All');

  useEffect(() => {
    if (errorRows?.indices.length) setFilter('All');
  }, [errorRows]);
  const listAddRef = useRef<((defaultValue?: any) => void) | null>(null);
  const readonly = mode === CrudMode.VIEW;
  const registrationType = Form.useWatch(['registrationDetails', 'type'], form);
  const isFcaRegistered = registrationType !== 'NOT_FCA_REGISTERED' && registrationType != null;
  // preserve: true is required so isActive is still visible when readonly (no Switch is
  // registered to mount that field, and useWatch otherwise only sees registered fields).
  const tradingNames = Form.useWatch(['tradingNames'], { form, preserve: true }) ?? [];
  const totalCount = tradingNames.length;
  const activeCount = tradingNames.filter((item: TradingAddress) => item?.isActive !== false).length;
  const inactiveCount = tradingNames.filter((item: TradingAddress) => item?.isActive === false).length;

  const onCreate = useCallback(() => {
    listAddRef.current?.({ isActive: true });
  }, []);

  const generalErrors = errorRows?.messages ?? [];

  return (
    <CrudFormCard
      icon={<IconMapPins />}
      title={`${t('lender.firms.labels.tradingNames')}:`}
      extra={
        <Flex gap="8px" justify="space-between" className={styles.tradingNamesExtra}>
          <Flex align="center" gap="8px">
            <Radio.Group
              value={filter}
              onChange={e => setFilter(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              className={styles.filterRadioGroup}
              options={[
                { label: `${t('global.labels.all')} (${totalCount})`, value: 'All' },
                { label: `${t('lender.firms.labels.active')} (${activeCount})`, value: 'Active' },
                { label: `${t('lender.firms.labels.inactive')} (${inactiveCount})`, value: 'Inactive' },
              ]}
            />
          </Flex>
          {!readonly && filter !== 'Inactive' && (
            <Button icon={<PlusOutlined />} onClick={onCreate} size="small">
              {t('global.buttons.add')}
            </Button>
          )}
        </Flex>
      }
      bodyScroll
    >
      {generalErrors.length > 0 && (
        <div data-form-general-error className={styles.generalErrors}>{generalErrors.join('\n')}</div>
      )}
      <Form.List name="tradingNames">
        {(fields, { add, remove }) => {
          listAddRef.current = add;
          return fields.map(field => {
            const row = tradingNames[field.name];
            const isActive = row?.isActive !== false;
            const isUsed = !!row?.isUsed;
            const isPersisted = !!row?.id;
            const isLastActiveAddress = isActive && activeCount <= 1;
            const deleteDisabled = isUsed || isLastActiveAddress;
            const deleteTooltip = isUsed
              ? t('lender.firms.labels.tradingNameDeleteTooltip')
              : isLastActiveAddress
                ? t('lender.firms.labels.tradingNameLastDeleteTooltip')
                : undefined;
            const show = filter === 'All' || (filter === 'Active' ? isActive : !isActive);

            return (
              <div key={field.key} className={styles.tradingName} style={show ? undefined : { display: 'none' }}>
                <div className={styles.tradingNameBody}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item
                      name={[field.name, 'name']}
                      label={
                        <>
                          {t('lender.firms.labels.name')}
                          {isFcaRegistered && isActive && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {t('lender.firms.labels.fca')}
                            </Tag>
                          )}
                        </>
                      }
                      required={!readonly}
                      style={{ flex: 1, marginBottom: 16 }}
                    >
                      <Input readOnly={readonly} {...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT)} />
                    </Form.Item>
                    {readonly ? (
                      <Form.Item label={<>{t('lender.firms.labels.status')}</>} style={{ flex: 1, marginBottom: 16 }}>
                        <Tag color={isActive ? 'green' : 'red'}>
                          {isActive ? t('lender.firms.labels.active') : t('lender.firms.labels.inactive')}
                        </Tag>
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name={[field.name, 'isActive']}
                        label={<>{t('lender.firms.labels.status')}</>}
                        tooltip={isUsed ? t('lender.firms.labels.tradingNameDeactivateTooltip') : undefined}
                        valuePropName="checked"
                        style={{ flex: 1, marginBottom: 16 }}
                      >
                        <Switch
                          checkedChildren={t('lender.firms.labels.active')}
                          unCheckedChildren={t('lender.firms.labels.inactive')}
                          disabled={isUsed}
                        />
                      </Form.Item>
                    )}
                  </div>
                  <AddressField
                    readonly={readonly}
                    form={form}
                    getAddress={() => form?.getFieldValue(['tradingNames', field.name])}
                    fcaModifiedFields={fcaModifiedFields}
                    removeFcaModifiedField={removeFcaModifiedField}
                    isFromFca={isActive}
                    namePrefix={[field.name]}
                    markRequired={!readonly}
                    initialEditing={false}
                    forceEditing={errorRows?.indices.includes(field.name) ? errorRows.version : 0}
                    enableLookup
                    lookupBasePath={['tradingNames', field.name]}
                    onAddressApplied={() => setDisableSave?.(false)}
                  />
                  <Form.Item name={[field.name, 'bdm']} style={{ marginBottom: 16 }}>
                    <BDMField
                      readonly={readonly}
                      regions={regions}
                      bdms={bdms}
                      loading={regionsAndBdmsLoading}
                      autoSelectSingleRegion={!isPersisted}
                    />
                  </Form.Item>
                </div>
                {!readonly && (
                  <Tooltip title={deleteTooltip}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      disabled={deleteDisabled}
                      title={t('global.buttons.delete')}
                    />
                  </Tooltip>
                )}
              </div>
            );
          });
        }}
      </Form.List>
    </CrudFormCard>
  );
};
